import { skipToken } from '@tanstack/react-query';
import ReviewPayments from 'components/ReviewPayments';
import { useLayoutContext } from 'contexts/LayoutContext';
import { PaymentActivity } from 'further-types/lp/bank-reconciliation';
import {
  IncomeOrExpenseApportionmentMode,
  IncomeOrExpenseType,
} from 'further-types/lp/income-and-expenses';
import {
  IncomeAndExpensesLabels,
  IncomeOrExpenseStateLabels,
} from 'further-ui/labels';
import { numberToCurrencyString } from 'further-ui/utils';
import api from 'lib/trpcClient';
import { useParams } from 'react-router-dom';

import { Divider, Link, Typography } from '@mui/material';
import { INCOME_OR_EXPENSE_LIST } from 'adminConstants/routes';
import Page from 'components/new/Page';
import PageLoader from 'components/PageLoader';
import { Column } from 'further-ui/components/Layout';
import StaticField from 'further-ui/components/StaticField';
import ApportionmentSummary from '../shared/ApportionmentSummary';

const ViewIncomeOrExpense: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId, vehicle } = useLayoutContext();

  const [incomeExpenseData] = api.incomeAndExpenses.get.useSuspenseQuery({
    id: id ?? '',
    vehicleId: selectedVehicleId ?? '',
  });

  const apportionmentModeIsMultiple =
    incomeExpenseData?.apportionmentMode ===
    IncomeOrExpenseApportionmentMode.Multiple;
  const apportionmentModeIsSingle =
    incomeExpenseData?.apportionmentMode ===
    IncomeOrExpenseApportionmentMode.Single;

  const completedAllocationValues = incomeExpenseData
    ? {
        allocations: incomeExpenseData.allocations.map((allocation) => ({
          investorLabel: allocation.investorLabel,
          commitmentId: allocation.commitmentId,
          equalisationId: allocation.equalisationId,
          totalCommitmentLcy: allocation.totalCommitmentLcy || 0,
          unallocatedCommitmentLcy: allocation.unallocatedCommitmentLcy || 0,
          allocationAmountTcy: allocation.allocationAmountTcy,
          allocationPercentage: allocation.allocationPercentage,
          excludeFromAllocation: allocation.excludeFromAllocation,
          allocationRule: allocation.allocationRule,
        })),
        allocationRule: incomeExpenseData.allocationRule,
      }
    : null;

  const hasApportionmentPeriods =
    apportionmentModeIsMultiple &&
    incomeExpenseData?.apportionmentBasis &&
    incomeExpenseData?.endDate &&
    completedAllocationValues;

  const {
    data: apportionmentPeriodsData,
    isLoading: apportionmentPeriodsLoading,
  } = api.incomeAndExpenses.apportionmentPeriods.useQuery(
    hasApportionmentPeriods
      ? {
          vehicleId: incomeExpenseData.vehicleId,
          apportionmentMode: incomeExpenseData.apportionmentMode,
          apportionmentBasis: incomeExpenseData.apportionmentBasis!,
          apportionmentStartDate: incomeExpenseData.startDate,
          apportionmentEndDate: incomeExpenseData.endDate,
          amountIncVatTcy: incomeExpenseData.amountIncVatTcy,
          amountExVatTcy: incomeExpenseData.amountExVatTcy,
          currency: incomeExpenseData.currencyCode,
          allocations: incomeExpenseData.allocations,
        }
      : skipToken,
  );

  const shouldHidePayments = (
    payments: Array<{
      activity: PaymentActivity;
      amountTcy: number;
    }>,
    amountTcy: number,
  ) => {
    return (
      payments.find((payment) => payment.activity === PaymentActivity.Created)
        ?.amountTcy !== amountTcy
    );
  };

  const getTransactionIdToDisplay = (
    transactions?: Array<{ reconciledAmountTcy?: number; id?: string }>,
  ) => {
    const reconciledTransaction = transactions?.find(
      (t) => t.reconciledAmountTcy,
    );
    return reconciledTransaction?.id ?? transactions?.[0]?.id ?? '';
  };

  if (apportionmentPeriodsLoading) {
    return <PageLoader />;
  }

  const actionType =
    incomeExpenseData.incomeOrExpenseType === IncomeOrExpenseType.Income
      ? 'Income'
      : 'Expense';

  return (
    <Page
      tabTitle="Income & Expenses: View Income & Expenses"
      heading={`View ${actionType}`}
      goBackAction={INCOME_OR_EXPENSE_LIST}
      spacing="lg"
      withHeadingDivider
    >
      <StaticField label="Vehicle">{vehicle?.vehicleName}</StaticField>
      <StaticField label="Counterparty">
        {incomeExpenseData.counterparty?.name}
      </StaticField>
      <StaticField label="Type">
        {IncomeAndExpensesLabels[incomeExpenseData.incomeOrExpenseType] ??
          incomeExpenseData.incomeOrExpenseType}
      </StaticField>
      <StaticField label="State">
        {IncomeOrExpenseStateLabels[incomeExpenseData.incomeOrExpenseState]}
      </StaticField>
      <StaticField label="Amount">
        {numberToCurrencyString(
          incomeExpenseData.incomeOrExpenseType === IncomeOrExpenseType.Income
            ? incomeExpenseData.amountIncVatTcy
            : -incomeExpenseData.amountIncVatTcy,
          {
            currency: incomeExpenseData.currencyCode,
            negativeInBrackets: true,
          },
        )}
      </StaticField>

      {incomeExpenseData.invoiceFile && (
        <StaticField label="Invoice file">
          <Link
            href={incomeExpenseData.invoiceFile.preview}
            target="_blank"
            rel="noopener noreferrer"
          >
            {incomeExpenseData.invoiceFile.name}
          </Link>
        </StaticField>
      )}

      {apportionmentModeIsMultiple && !!apportionmentPeriodsData?.length && (
        <ApportionmentSummary
          apportionmentPeriods={apportionmentPeriodsData}
          currency={incomeExpenseData.currencyCode}
        />
      )}

      {apportionmentModeIsSingle && (
        <>
          <Divider />
          <Column spacing="sm">
            <Typography variant="h5">Payment record</Typography>
            <ReviewPayments
              transactionId={getTransactionIdToDisplay(
                incomeExpenseData.apportionmentPeriods[0]?.transactions,
              )}
              vehicleId={vehicle?._id ?? ''}
              currencyCode={incomeExpenseData.currencyCode}
              amountTcy={incomeExpenseData.amountIncVatTcy}
              hidePayments={shouldHidePayments}
            />
          </Column>
        </>
      )}
    </Page>
  );
};

export default ViewIncomeOrExpense;
